import { AbilityBuilder, CreateAbility, PureAbility, createMongoAbility } from "@casl/ability";

import { TDbUser } from "@/shared/hooks/auth/currentUser.hook";

import { ClientRoleEnum } from "@/modules/client/client-roles.interface";
import { IClient } from "@/modules/client/clients.interface";
import { AssessmentRoleEnum } from "@/modules/assessments/services/assessment-permissions-http.service";

export enum Subject {
  ALL = "all",
  ASSESSMENT = "assessment",
  ASSESSMENT_INVITE = "assessment_invite",
  ASSESSMENT_USER_PERMISSION = "assessment_user_permission",
  CLIENT = "client",
  CLIENT_USER = "client_user",
  CLIENT_TEXT = "client_text",
  REPORT_SETTING = "report_setting",
  USER_SETTING = "user_setting",
  USER_NOTIFICATION_SETTING = "user_notification_setting",
  ASSESSMENT_TEMPLATE = "assessment_template",
  TEST = "test",
  NORM = "norm",
}

export enum Action {
  Manage = "manage",
  Create = "create",
  Read = "read",
  Update = "update",
  Delete = "delete",
  Rescind = "rescind",
  Rescore = "rescore",
  Resend = "resend",
  Reset = "reset",
}

export type AppAbility = PureAbility<[Action, Subject]>;

export class CaslAbilityFactory {
  private readonly abilityBuilder: AbilityBuilder<PureAbility<[Action, Subject]>>;

  public constructor() {
    this.abilityBuilder = new AbilityBuilder(createMongoAbility as CreateAbility<AppAbility>);
  }

  public buildForUser() {
    return this.abilityBuilder.build();
  }

  public createForClientUser({
    clientRoles,
    user,
    client,
  }: {
    clientRoles: ClientRoleEnum[];
    user?: Pick<TDbUser, "isAdmin">;
    client?: Pick<IClient, "insightsEnabled">;
  }) {
    const { can, cannot } = this.abilityBuilder;

    clientRoles.forEach((roleName) => {
      if (roleName === ClientRoleEnum.ADMIN) {
        can(Action.Manage, Subject.ASSESSMENT_INVITE);
        can(Action.Read, Subject.ASSESSMENT);
        can(Action.Manage, Subject.ASSESSMENT_USER_PERMISSION);
        can(Action.Read, Subject.CLIENT);
        can(Action.Manage, Subject.CLIENT_USER);
        can(Action.Update, Subject.CLIENT, ["logoUrl"]);
        can(Action.Read, Subject.USER_NOTIFICATION_SETTING);
        can(Action.Update, Subject.USER_NOTIFICATION_SETTING);
        can(Action.Read, Subject.USER_SETTING);

        cannot(Action.Rescore, Subject.ASSESSMENT_INVITE);
        cannot(Action.Read, Subject.ASSESSMENT_INVITE, ["timerDisabled"]);
        cannot(Action.Update, Subject.ASSESSMENT_INVITE, ["timerDisabled", "claimedByUser.fullName"]);
        cannot(Action.Delete, Subject.CLIENT_USER);
        cannot(Action.Reset, Subject.ASSESSMENT_INVITE);
        cannot(Action.Read, Subject.CLIENT, ["status", "insightsEnabled"]);
        cannot(Action.Update, Subject.CLIENT, ["status", "insightsEnabled"]);
      }

      if (roleName === ClientRoleEnum.ASSESSMENT_MANAGER) {
        can(Action.Read, Subject.ASSESSMENT);
        can(Action.Read, Subject.ASSESSMENT_INVITE);
        can(Action.Create, Subject.ASSESSMENT_INVITE);
        can(Action.Update, Subject.ASSESSMENT_INVITE);
        can(Action.Delete, Subject.ASSESSMENT_INVITE);
        can(Action.Rescind, Subject.ASSESSMENT_INVITE);
        can(Action.Resend, Subject.ASSESSMENT_INVITE);

        cannot(Action.Read, Subject.ASSESSMENT_INVITE, ["timerDisabled"]);
        cannot(Action.Update, Subject.ASSESSMENT_INVITE, ["timerDisabled", "claimedByUser.fullName"]);
      }

      if (roleName === ClientRoleEnum.ASSESSMENT_TEAM_MEMBER) {
        can(Action.Read, Subject.ASSESSMENT);
      }
    });

    if (!client?.insightsEnabled) {
      cannot(Action.Read, Subject.ASSESSMENT_INVITE, ["insights"]);
    }

    if (user) {
      this.createForUser(user);
    }

    return this.buildForUser();
  }

  public createForAssessmentUser(assessmentRoles: AssessmentRoleEnum[], user?: Pick<TDbUser, "isAdmin">) {
    const { can, cannot } = this.abilityBuilder;

    assessmentRoles.forEach((roleName) => {
      if (roleName === AssessmentRoleEnum.RECRUITER) {
        can(Action.Read, Subject.ASSESSMENT);
        can(Action.Read, Subject.ASSESSMENT_INVITE);
        can(Action.Create, Subject.ASSESSMENT_INVITE);
        can(Action.Update, Subject.ASSESSMENT_INVITE);
        can(Action.Delete, Subject.ASSESSMENT_INVITE);
        can(Action.Rescind, Subject.ASSESSMENT_INVITE);
        can(Action.Resend, Subject.ASSESSMENT_INVITE);

        cannot(Action.Read, Subject.ASSESSMENT_INVITE, ["timerDisabled", "insights"]);
        cannot(Action.Update, Subject.ASSESSMENT_INVITE, ["timerDisabled", "claimedByUser.fullName"]);
      }

      if (roleName === AssessmentRoleEnum.INVITER) {
        can(Action.Read, Subject.ASSESSMENT);
        can(Action.Read, Subject.ASSESSMENT_INVITE);
        can(Action.Create, Subject.ASSESSMENT_INVITE);
        can(Action.Update, Subject.ASSESSMENT_INVITE);
        can(Action.Delete, Subject.ASSESSMENT_INVITE);
        can(Action.Rescind, Subject.ASSESSMENT_INVITE);
        can(Action.Resend, Subject.ASSESSMENT_INVITE);

        cannot(Action.Read, Subject.ASSESSMENT_INVITE, ["timerDisabled", "reports", "insights"]);
        cannot(Action.Update, Subject.ASSESSMENT_INVITE, ["timerDisabled", "claimedByUser.fullName"]);
      }

      if (roleName === AssessmentRoleEnum.HIRING_MANAGER) {
        can(Action.Read, Subject.ASSESSMENT);
        can(Action.Read, Subject.ASSESSMENT_INVITE);

        cannot(Action.Read, Subject.ASSESSMENT_INVITE, ["timerDisabled", "insights"]);
      }
    });

    if (user) {
      this.createForUser(user);
    }

    return this.buildForUser();
  }

  private createForUser(user: Pick<TDbUser, "isAdmin">) {
    const { can, cannot } = this.abilityBuilder;

    if (user.isAdmin) {
      can(Action.Manage, Subject.ALL); // read-write access to everything
      cannot(Action.Read, Subject.USER_NOTIFICATION_SETTING);
      cannot(Action.Update, Subject.USER_NOTIFICATION_SETTING);
      cannot(Action.Read, Subject.USER_SETTING);
    }

    return this.buildForUser();
  }
}
